import { useState, useEffect } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import BaseWrapper from "../../components/shared/BaseWrapper";
import { patchContractItem } from "../API/billingAPI";
import billingValidationSchema from "../../components/Billing/billingValidationSchema";
import ContractTerminateStatusDialog from "./components/ContractTerminateStatusDialog";
import { dateOnlyToDate, formatDateInPhilippines, toStartOfPhilippineDay, todayInPhilippines } from "./utils/phDate";
import {
  Box,
  Button,
  TextField,
  Grid,
  Typography,
  Select,
  MenuItem,
  FormControl,
  InputAdornment,
  InputLabel,
  CircularProgress,
  CardHeader,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from "@mui/material";

const toStartOfDay = (value) => {
  return toStartOfPhilippineDay(value);
}

const formatDate = formatDateInPhilippines;

const deriveContractStatus = (startDate, endDate) => {
  if (!startDate) return 'INACTIVE';
  const today = toStartOfDay(todayInPhilippines());
  const start = toStartOfDay(startDate);
  const end = endDate ? toStartOfDay(endDate) : null;

  if (start > today ) return 'INACTIVE';
  if (end && today > end) return 'EXPIRED';
  return "ACTIVE";
}

const PAYMENT_INTERVALS = {
  ONE_TIME: null,
  WEEKLY: null,
  MONTHLY: 1,
  QUARTERLY: 3,
  "SEMI-ANNUAL": 6,
  ANNUAL: 12,
  CUSTOM: null,
};

function EditContract() {
  const navigate = useNavigate();
  const { contractId } = useParams();
  const location = useLocation();
  const passedContract = location.state?.contract;
  const selectedClient = location.state?.selectedClient;
  const source = location.state?.source || "manageContracts";
  const contractSource = location.state?.contractSource;
  const contractParamId = passedContract?.contract_id || passedContract?.id || contractId;
  const backRoute =
    source === 'manageClients' ? '/manage-clients' :
    source === 'viewContract' ? `/view-contract/${contractParamId}` :
    '/manage-contracts';
  const backState = 
    source === 'viewContract'
      ? { contract: passedContract, selectedClient, source: contractSource }
      : { selectedClient };

  const parseDate = (dateStr) => {
    if (!dateStr) return null;
    if (dateStr instanceof Date) return dateStr;
    
    try {
      const [year, month, day] = dateStr.split('-');
      if (year && month && day) {
        return dateOnlyToDate(`${year}-${month}-${day}`);
      }
      return dateOnlyToDate(dateStr);
    } catch (err) {
      return null;
    }
  };

  const createInitialContract = () => ({
    contract_id: passedContract?.contract_id || passedContract?.id || contractId || "",
    client_id: passedContract?.client_id || "",
    client_name: passedContract?.client_name || "",
    price_minor: passedContract?.price_minor || 0,
    currency: passedContract?.currency || "PHP",
    payment_terms: passedContract?.payment_terms || {
      type: "MONTHLY",
      interval_months: 1,
      count: 12,
      notes: "",
    },
    contract_start: parseDate(passedContract?.contract_start),
    contract_end: parseDate(passedContract?.contract_end),
    status: passedContract?.status || "ACTIVE",
  });

  const [ errors, setErrors ] = useState({});
  const [ loading, setLoading ] = useState(false);
  const [ successMessage, setSuccessMessage ] = useState("");
  const [ form, setForm ] = useState(createInitialContract());
  const [originalForm] = useState(createInitialContract);
  const [ hasChanges, setHasChanges ] = useState(false);
  const [ cancelDialogOpen, setCancelDialogOpen ] = useState(false);
  const [ terminateDialogOpen, setTerminateDialogOpen ] = useState(false);

  // Detect changes to dates to derive status
  useEffect(() => {
    if (form.status === 'TERMINATED') return;
    const autoStatus = deriveContractStatus(form.contract_start, form.contract_end);
    setForm((prev) =>
      prev.status === autoStatus ? prev : { ...prev, status: autoStatus }
    );
  }, [form.contract_start, form.contract_end, form.status]);

  // Detect changes whenever form updates
  useEffect(() => {
    const currentComparable = {
      ...form,
      contract_start: formatDateInPhilippines(form.contract_start),
      contract_end: formatDateInPhilippines(form.contract_end),
    };

    const originalComparable = {
      ...originalForm,
      contract_start: formatDateInPhilippines(originalForm.contract_start),
      contract_end: formatDateInPhilippines(originalForm.contract_end),
    };

    setHasChanges(
      JSON.stringify(currentComparable) !== JSON.stringify(originalComparable)
    );
  }, [form, originalForm]);

  // Handler functions
  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await billingValidationSchema.validate(form, { abortEarly: false });
      setLoading(true);

      const stored = localStorage.getItem('user');
      const storedUser = stored ? JSON.parse(stored) : null;
      const userInfo = storedUser ? {
        user_id: storedUser.username || storedUser.user_id || '',
        name: `${storedUser.firstname || ''} ${storedUser.lastname || ''}`.trim()
      }
      : { user_id: 'system', name: 'system'};

      const contractStart = formatDate(form.contract_start);
      const contractEnd = formatDate(form.contract_end);
      const originalContractStart = formatDate(originalForm.contract_start);
      const originalContractEnd = formatDate(originalForm.contract_end);

      const changedData = {};

      if (form.price_minor !== originalForm.price_minor) {
        changedData.price_minor = parseInt(form.price_minor) || 0;
      }

      if (form.currency !== originalForm.currency) {
        changedData.currency = form.currency;
      }

      if (contractStart !== originalContractStart) {
        changedData.contract_start = contractStart;
      }

      if (contractEnd !== originalContractEnd) {
        changedData.contract_end = contractEnd;
      }

      if (form.status !== originalForm.status) {
        changedData.status = form.status;
      }

      if (JSON.stringify(form.payment_terms) !== JSON.stringify(originalForm.payment_terms)) {
        changedData.payment_terms = form.payment_terms;
      }

      if (Object.keys(changedData).length === 0) {
        setErrors({ form: "No contract changes detected." });
        setLoading(false);
        return;
      }

      const submitData = {
        ...changedData,
        user_id: userInfo.user_id,
        user_name: userInfo.name,
      };

      const res = await patchContractItem(form.client_id, form.contract_id, submitData);

      setLoading(false);
      if (res?.ok || res?.contract || res) {
        const updatedContract = res.contract || res.data?.contract || {
          ...passedContract,
          ...changedData,
          contract_id: form.contract_id,
          client_id: form.client_id,
          client_name: form.client_name,
        };

        setSuccessMessage("Contract updated successfully!");

        setTimeout(() => {
          navigate(backRoute, {
            state:
              source === "viewContract"
                ? {
                    contract: updatedContract,
                    selectedClient,
                    source: contractSource,
                  }
                : {
                    selectedClient,
                    updatedContract,
                    refresh: true,
                  },
          });
        }, 1500);
      }
    } catch (err) {
      if (err.inner) {
        const map = {};
        err.inner.forEach((e) => { map[e.path] = e.message });
        setErrors(map);
      } else {
        setErrors({ form: err.message || "Validation error" });
      }
      setLoading(false);
    }
  };

  const handleFieldChange = (field, value) => {
    setForm((prev) => ({
      ...prev,
      [field]: value,
    }));

    setErrors((prev) => ({
      ...prev,
      [field]: null,
      form: null,
    }));
  };

  const handlePaymentTermsChange = (field, value) => {
    setForm((prev) => {
      const nextPaymentTerms = {
        ...prev.payment_terms,
        [field]:
          field === "count"
            ? Number(value)
            : value,
      };

      if (field === "type") {
        nextPaymentTerms.interval_months = PAYMENT_INTERVALS[value];
      }

      return {
        ...prev,
        payment_terms: nextPaymentTerms,
      };
    });

    setErrors((prev) => ({
      ...prev,
      payment_terms: null,
      form: null,
    }));
  };

  const handleCancel = () => {
    if (hasChanges) {
      setCancelDialogOpen(true);
    } else {
      navigate(backRoute, { state: backState });
    }
  };

  const handleConfirmCancel = () => {
    setCancelDialogOpen(false);
    navigate(backRoute, { state: backState });
  }

  const handleTerminateSuccess = () => {
    setForm((prev) => ({ ...prev, status: 'TERMINATED' }));
  };

  const handlePriceInput = (e) => {
    const digits = e.target.value.replace(/\D/g, '');
    handleFieldChange('price_minor', parseInt(digits || '0', 10));
  }

  return (
    
    <BaseWrapper
      titleIcon={<ReceiptLongIcon />}
      title="Billing"
      subTitle="Edit Contract"
      subTitleRoute={"/manage-contracts"}
      successMessage={successMessage}
      setSuccessMessage={setSuccessMessage}
    >
      <Box component="form" onSubmit={handleSubmit} sx={{ p:2 }}>
        <Grid container spacing={2}>
          {/* Contract Details Haeader */}
          <Grid item xs={12}>
            <CardHeader
              title={
                <Typography
                  variant='h6'
                  sx={{
                    paddingBottom: '0.5rem',
                    fontWeight: 'bold',
                    textTransform: 'uppercase',
                    fontSize: '1rem',
                  }}
                >
                  Contract Details
                </Typography>
              }
              sx={{ pb: 0, pt: 0 }}
            />
          </Grid>
          {/* Client ID  (Read-only) */}
          <Grid item xs={12}>
            <TextField
              label="Client ID"
              value={form.client_id}
              fullWidth
              disabled
              variant="outlined"
            />
          </Grid>
          {/* Client Name  (Read-only) */}
          <Grid item xs={12}>
            <TextField
              label="Client Name"
              value={form.client_name || "N/A"}
              fullWidth
              disabled
              variant="outlined"
            />
          </Grid>
          {/* Contract Id (Read-only) */}
          <Grid item xs={12}>
            <TextField
              label="Contract ID"
              value={form.contract_id}
              fullWidth
              disabled
              variant="outlined"
            />
          </Grid>

          {/* Price */}
          <Grid item xs={12} sm={6}>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
              <TextField 
                label='Price'
                type='text'
                value={(form.price_minor / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                onChange={handlePriceInput}
                sx={{ flex: 1 }}
                inputProps={{ inputMode: 'numeric' }}
                InputProps={{
                  startAdornment: <InputAdornment position='start'>PHP</InputAdornment>
                }}
                error={!!errors.price_minor}
                helperText={errors.price_minor}
              />
            </Box>
          </Grid>
          {/* Contract Start Date */}
          <Grid item xs={12} sm={6}>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DatePicker
                label="Contract Start"
                value={form.contract_start}
                onChange={(date) => handleFieldChange("contract_start", date)}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    error: !!errors.contract_start,
                    helperText: errors.contract_start
                  }
                }}
              />
            </LocalizationProvider>
          </Grid>
          {/* Contract End Date */}
          <Grid item xs={12} sm={6}>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DatePicker
                label="Contract End"
                value={form.contract_end}
                onChange={(date) => handleFieldChange("contract_end", date)}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    error: !!errors.contract_end,
                    helperText: errors.contract_end
                  }
                }}
              />
            </LocalizationProvider>
          </Grid>

          {/* Status */}
          <Grid item xs={12} sm={6}>
            <TextField
              label='Status'
              value={form.status}
              fullWidth
              disabled
              variant='outlined'
              helperText={
                form.status !== 'TERMINATED' 
                  ? 'Automatically derived from contract start/end dates'
                  : 'This contract has been terminated'
              }
            />
            {form.status !== 'TERMINATED' && (
              <Button
                variant='outlined'
                size='small'
                sx={{ mt: 1 }}
                onClick={() => setTerminateDialogOpen(true)}
              >
                Terminate
              </Button>
            )}
          </Grid>
          
          {/* Payment Terms Section Header */}
          <Grid item xs={12}>
            <CardHeader
              title={
                <Typography
                  variant='h6'
                  sx={{
                    paddingBottom: '0.5rem',
                    fontWeight: 'bold',
                    textTransform: 'uppercase',
                    fontSize: '1rem',
                  }}
                >
                  Payment Terms
                </Typography>
              }
              sx={{ pb: 0, pt: 0, mt: 2 }}
            />
          </Grid>
          
          {/* Payment Type */}
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <InputLabel>Type</InputLabel>
              <Select
                value={form.payment_terms.type}
                onChange={(e) => handlePaymentTermsChange("type", e.target.value)}
                label="Type"
              >
                <MenuItem value="ONE_TIME">One-time payment</MenuItem>
                <MenuItem value="WEEKLY">Weekly</MenuItem>
                <MenuItem value="MONTHLY">Monthly</MenuItem>
                <MenuItem value="QUARTERLY">Quarterly</MenuItem>
                <MenuItem value="SEMI-ANNUAL">Semi-Annual</MenuItem>
                <MenuItem value="ANNUAL">Annual</MenuItem>
                <MenuItem value="CUSTOM">CUSTOM</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          {/* Count */}      
          <Grid item xs={12} sm={6}>
            <TextField 
              label="Count (number of payments)"
              type="number"
              value={form.payment_terms.count}
              onChange={(e) => handlePaymentTermsChange("count", e.target.value)}
              fullWidth
              multiline
              rows={2}
            />
          </Grid>
          
          {/* Notes */}
          <Grid item xs={12} sm={6}>
            <TextField
              label="Notes"
              value={form.payment_terms.notes}
              onChange={(e) => handlePaymentTermsChange("notes", e.target.value)}
              fullWidth
              multiline
              rows={2}
            />
          </Grid>

          {/* Submit and Cancel Buttons */}
          {errors.form && (
            <Grid item xs={12}>
              <Typography color="error" variant="body2">
                {errors.form}
              </Typography>
            </Grid>
          )}
          <Grid item xs={12}>
            {hasChanges && (
              <Button
                variant="contained"
                type="submit"
                disabled={loading}
                sx={{ mr: 2 }}
              >
                {loading && <CircularProgress size={20} sx={{ mr: 1 }} />}
                {loading ? "Updating..." : "Update Contract"}
              </Button>
            )}
            <Button
              variant="outlined"
              onClick={handleCancel}
              disabled={loading}
            >
              Cancel
            </Button>
          </Grid>
        </Grid>
      </Box>
      <Dialog open={cancelDialogOpen} onClose={() => setCancelDialogOpen(false)}>
        <DialogTitle>Discard changes?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            You have unsaved changes. If you leave now, your changes will be lost.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCancelDialogOpen(false)}>Stay</Button>
          <Button onClick={handleConfirmCancel} color='error'>Discard</Button>
        </DialogActions>
      </Dialog>

      {/* Set Terminate Contract */}
      <ContractTerminateStatusDialog 
        open={terminateDialogOpen}
        onClose={() => {
          setTerminateDialogOpen(false);
        }}
        contract={form}
        onTerminateSuccess={() => {
          setTerminateDialogOpen(false);
          handleTerminateSuccess();
        }}
      />
    </BaseWrapper>
  );
}

export default EditContract;
